import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Clock, ChevronRight, ShieldCheck, Sparkles } from 'lucide-react';
import { courses as mockCourses } from '../mock';

const CourseCard = ({ course, onEnrollClick }) => {
  return (
    <div
      data-testid={`course-card-${course.slug}`}
      className="group relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-6 border border-slate-700 hover:border-purple-500/50 transform hover:-translate-y-2 hover:scale-[1.02] transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-purple-500/20 flex flex-col"
    >
      {/* Top badges */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {course.addOn && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-amber-500/15 text-amber-300 border border-amber-400/30">
            <Sparkles className="h-3.5 w-3.5" />
            Skill Upgrade
          </span>
        )}
        {course.featured && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md">
            ⭐ POPULAR
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors duration-300">
        {course.title}
      </h3>
      <p className="text-sm text-purple-300/80 mb-3 italic">{course.tagline}</p>

      <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
        <Clock className="h-4 w-4" />
        <span>{course.duration}</span>
      </div>

      {/* Short description */}
      <p className="text-gray-300 mb-5 leading-relaxed flex-grow">{course.description}</p>

      {/* Module preview chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        {course.modules.slice(0, 4).map((m, i) => (
          <span
            key={i}
            className="px-2.5 py-1 text-[11px] font-medium bg-white/5 border border-white/10 text-gray-300 rounded-md"
          >
            {m.name.length > 22 ? `${m.name.slice(0, 22)}…` : m.name}
          </span>
        ))}
        {course.modules.length > 4 && (
          <span className="px-2.5 py-1 text-[11px] font-medium bg-white/5 border border-white/10 text-gray-400 rounded-md">
            +{course.modules.length - 4} more
          </span>
        )}
      </div>

      {/* Buttons */}
      <div className="space-y-3">
        <Link to={`/courses/${course.slug}`} className="block" data-testid={`course-card-view-${course.slug}`}>
          <Button className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 py-5 rounded-xl font-semibold transform hover:scale-[1.02] transition-all duration-300">
            View Course Details
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
        <Button
          onClick={onEnrollClick}
          data-testid={`course-card-fee-${course.slug}`}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-5 rounded-xl font-semibold transform hover:scale-[1.02] transition-all duration-300 border-0"
        >
          Request Fee Details
        </Button>
      </div>
    </div>
  );
};

export const Courses = ({ onEnrollClick }) => {
  const shortCourses = mockCourses.filter((c) => c.tier === 'short').sort((a, b) => a.order - b.order);
  const diplomaCourses = mockCourses.filter((c) => c.tier === 'diploma').sort((a, b) => a.order - b.order);

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-6 py-2 bg-purple-500/10 backdrop-blur-md border border-purple-500/30 rounded-full">
            <span className="text-purple-400 font-semibold text-sm tracking-wide">OUR PROGRAMS</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Courses Designed for
            <span className="block bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent mt-2">
              Your Success
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-6">
            From a focused 1-month skill upgrade to a 14-month diploma with internship. Pick the path that fits your goal.
          </p>

          {/* Pricing + Placement guarantee — stacked, with breathing room */}
          <div className="flex flex-col items-center gap-3 mt-4">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-orange-500/15 to-pink-500/15 border border-orange-400/30">
              <span className="text-2xl" aria-hidden>💰</span>
              <span className="text-orange-200 text-base md:text-lg font-bold tracking-wide">
                Courses start from <span className="text-white">INR 25,000</span>
              </span>
            </div>

            <div className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-emerald-500/10 backdrop-blur-md border border-emerald-500/30">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              <span className="text-emerald-300 text-base font-medium">
                100% Placement Assistance Guarantee<sup className="ml-1 text-amber-300">*</sup>
              </span>
            </div>

            <p className="text-xs text-gray-400 max-w-xl mx-auto italic mt-1">
              *Placement Assistance Guarantee is offered on select programs. Talk to the Admissions Office for details.
            </p>
          </div>
        </div>

        {/* Short Courses Tier */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8 max-w-7xl mx-auto">
            <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-purple-500/40"></div>
            <span className="text-purple-300 font-bold tracking-widest text-sm uppercase">Short Courses</span>
            <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-purple-500/40"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-6 max-w-7xl mx-auto">
            {shortCourses.map((course) => (
              <CourseCard key={course.id} course={course} onEnrollClick={onEnrollClick} />
            ))}
          </div>
        </div>

        {/* Diploma Programs Tier */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-8 max-w-7xl mx-auto">
            <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-orange-500/40"></div>
            <span className="text-orange-300 font-bold tracking-widest text-sm uppercase">Diploma Programs</span>
            <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-orange-500/40"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-6 max-w-7xl mx-auto">
            {diplomaCourses.map((course) => (
              <CourseCard key={course.id} course={course} onEnrollClick={onEnrollClick} />
            ))}
          </div>
        </div>

        {/* Additional Info */}
        <div className="text-center mt-16">
          <p className="text-gray-400 text-lg mb-6">
            Can&apos;t decide? Talk to Admissions Office. We&apos;ll help you pick the right program for your goals.
          </p>
          <Button
            onClick={onEnrollClick}
            size="lg"
            data-testid="courses-talk-to-admissions-btn"
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-10 py-6 text-lg rounded-full font-semibold transform hover:scale-105 transition-all duration-300 border-0"
          >
            Talk to Admissions Office
          </Button>
        </div>
      </div>
    </section>
  );
};
